/// <amd-module name="Artifacts/Connect/tools/maven/Maven" />
import { CopyableCode, DetailsCard, PublishDetailsCard } from "Artifacts/Connect/components/Common";
import * as Res from "Artifacts/Connect/Resources";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { IToolConnectProps } from "Artifacts/Connect/tools/IToolConnectProps";
import { MavenProtocol } from "Artifacts/Connect/tools/maven/Protocol";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { getCurrentCollectionName } from "Artifacts/Root/helpers/CollectionHelpers";
import { getIsUserMember } from "Artifacts/Root/helpers/UserHelpers";
import * as React from "react";
import { useMemo } from "react";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData } from "VSS/Platform/Page";
import { Link } from "VSSUI/Link";

const MavenConnect: React.FC<IToolConnectProps<MavenProtocol>> = ({
  pageContext,
  protocol,
  feed: feed,
  packagingService,
  tfsService,
}) => {
  const url = protocol.getFeedUrl(packagingService, feed);
  const tfsServiceUrl = tfsService.getSessionTokenUri();

  const { collectionName, userIsMember } = useMemo(() => {
    return {
      collectionName: getCurrentCollectionName(pageContext),
      userIsMember: getIsUserMember(pageContext),
    };
  }, [pageContext]);

  const serverId = feed.name;

  const pomText = `<repository>
  <id>${serverId}</id>
  <url>${url}</url>
  <releases>
    <enabled>true</enabled>
  </releases>
  <snapshots>
    <enabled>true</enabled>
  </snapshots>
</repository>`;

  const settingsXmlPreamble = `<settings xmlns="http://maven.apache.org/SETTINGS/1.0.0"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
          xsi:schemaLocation="http://maven.apache.org/SETTINGS/1.0.0
                              https://maven.apache.org/xsd/settings-1.0.0.xsd">
  <servers>`;
  const settingsXmlServer = `    <server>
      <id>${serverId}</id>
      <username>${collectionName}</username>
      <password>[${Res.PersonalAccessToken_Symbol}]</password>
    </server>`;
  const settingsXmlEpilogue = `  </servers>
</settings>`;

  if (userIsMember) {
    return (
      <React.Fragment>
        <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
          <p>
            <FormatComponent format={Res.ProjectSetup_Maven_Pom}>
              <strong>{Res.Both}</strong>
              <code>&lt;repositories&gt;</code>
              <code>&lt;distributionManagement&gt;</code>
            </FormatComponent>
          </p>
          <CopyableCode ariaLabel={Res.ProjectSetup}>
            <pre>{pomText}</pre>
          </CopyableCode>

          <p>
            <FormatComponent format={Res.ProjectSetup_MavenGradle_Settings}>
              <code>settings.xml</code>
              <code>{"${user.home}/.m2"}</code>
            </FormatComponent>
          </p>
          <CopyableCode text={settingsXmlServer} ariaLabel={Res.ProjectSetup}>
            <pre className="secondary-text">{settingsXmlPreamble}</pre>
            <pre>{settingsXmlServer}</pre>
            <pre className="secondary-text">{settingsXmlEpilogue}</pre>
          </CopyableCode>

          <p>
            <FormatComponent format={Res.GeneratePAT_MavenGradle}>
              <Link underline href={tfsServiceUrl} target="_blank">
                {Res.PersonalAccessToken}
              </Link>
              <em>{Res.Packaging}</em>
            </FormatComponent>
          </p>
        </DetailsCard>

        <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
          <p>{Res.RunCommand_Directory}</p>
          <CopyableCode ariaLabel={Res.RestorePackages}>mvn install</CopyableCode>
        </DetailsCard>

        <PublishDetailsCard feed={feed}>
          <p>{Res.RunCommand_Directory}</p>
          <CopyableCode ariaLabel={Res.PublishPackages}>mvn deploy</CopyableCode>
        </PublishDetailsCard>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
          <p>
            <FormatComponent format={Res.ProjectSetup_Maven_PomReplaceRepos}>
              <code>pom.xml</code>
              <code>&lt;repositories&gt;</code>
              <code>&lt;/dependencies&gt;</code>
            </FormatComponent>
          </p>
          <CopyableCode ariaLabel={Res.ProjectSetup}>
            <pre>{pomText}</pre>
          </CopyableCode>
        </DetailsCard>

        <DetailsCard title={{ text: Res.RestorePackages, ariaLevel: 3 }}>
          <p>{Res.RestorePackages_Maven_Details}</p>
          <CopyableCode ariaLabel={Res.RestorePackages}>mvn install</CopyableCode>
        </DetailsCard>
      </React.Fragment>
    );
  }
};

class MavenTool implements ITool {
  readonly id = ToolId.Maven;
  readonly name = "Maven";
  readonly protocol = new MavenProtocol();
  readonly allowNonMembers = true;
  readonly imagePath = `ms.azure-artifacts/connect/img/apache.svg`;
  readonly toolLinkId = 2103966;
  readonly Component: React.ComponentType = MavenConnect;

  isEnvironmentSupported(pageData: IPageData): boolean {
    return true;
  }

  public isProtocolEnabled(pageContext: IVssPageContext): boolean {
    return true;
  }

  supportsGet(): boolean {
    return true;
  }

  supportsPush(feed: ICombinedFeedView): boolean {
    return feed.canPublishPackages;
  }

  renderComponent(
    pageContext: IVssPageContext,
    feed: ICombinedFeedView,
    packagingService: PackagingService,
    tfsService: TfsService,
  ): JSX.Element {
    return (
      <MavenConnect
        feed={feed}
        protocol={this.protocol}
        pageContext={pageContext}
        packagingService={packagingService}
        tfsService={tfsService}
      />
    );
  }

  public getToolLink = () => ({
    href: `https://go.microsoft.com/fwlink/?linkid=${this.toolLinkId}`,
    children: `${Res.CommandLineReference}`,
  });
}

ToolService.registerTool(new MavenTool());
